import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Menu, ChevronDown, ChevronRight, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { LogoMark } from "@/components/site/Logo";
import { NAV_LINKS, CONTACT } from "@/data/content";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-navbar" className="fixed top-0 left-0 right-0 z-50">
      {/* Utility bar */}
      <div
        className={`overflow-hidden bg-forest text-sand transition-[max-height,opacity] duration-500 ${
          scrolled ? "max-h-0 opacity-0" : "max-h-16 opacity-100"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-10 flex items-center justify-between text-xs md:text-[13px]">
          <div className="flex items-center gap-5">
            <a href={CONTACT.phoneHref} className="flex items-center gap-2 hover:text-sage transition-colors">
              <Phone className="w-3.5 h-3.5" /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} className="hidden sm:flex items-center gap-2 hover:text-sage transition-colors">
              <Mail className="w-3.5 h-3.5" /> {CONTACT.email}
            </a>
          </div>
          <p className="hidden lg:block text-white font-semibold tracking-wide">
            Private pay &amp; most insurance plans accepted — including GreenShield
          </p>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-sand/95 backdrop-blur-xl border-b border-line shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
        <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-[68px] flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="logo-link"
            onClick={(e) => {
              // If already on Home, force scroll-to-top instead of noop
              if (window.location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="flex items-center gap-2.5 shrink-0 group"
          >
            <span className="w-10 h-10 rounded-full overflow-hidden shrink-0 transition-transform duration-300 ease-out group-hover:scale-125 group-hover:rotate-6 group-hover:shadow-[0_10px_25px_rgba(46,106,138,0.35)]">
              <LogoMark className="w-full h-full" />
            </span>
            <span className="leading-none transition-transform duration-300 group-hover:translate-x-0.5">
              <span className="block font-serif text-lg text-forest tracking-tight">New Beginnings</span>
              <span className="block text-[10px] uppercase tracking-[0.22em] text-ink2">Recovery Home</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden xl:flex items-center gap-0.5">
            {NAV_LINKS.map((item) =>
              item.children ? (
                <div key={item.label} className="relative group/nav">
                  <button
                    data-testid={`nav-${item.id}`}
                    className={
                      item.highlight
                        ? "flex items-center gap-1 whitespace-nowrap px-3 py-2 text-[14px] font-semibold rounded-full bg-terracotta/10 text-terracotta border border-terracotta/30 hover:bg-terracotta hover:text-white transition-colors mx-1"
                        : "flex items-center gap-1 whitespace-nowrap px-2.5 py-2 text-[14px] font-medium text-ink2 hover:text-forest transition-colors"
                    }
                  >
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover/nav:rotate-180" />
                  </button>
                  <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover/nav:opacity-100 group-hover/nav:visible group-hover/nav:translate-y-0 transition-all duration-200">
                    <div className="min-w-[260px] rounded-2xl bg-white border border-line shadow-[0_20px_40px_rgb(0,0,0,0.12)] p-2">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          data-testid={`dropdown-${c.id}`}
                          className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-ink2 hover:bg-sand hover:text-forest transition-colors group/item"
                        >
                          <span className="font-medium">{c.label}</span>
                          <ChevronRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`nav-${item.id}`}
                  className={
                    item.highlight
                      ? "whitespace-nowrap px-3 py-2 text-[14px] font-semibold rounded-full bg-terracotta/10 text-terracotta border border-terracotta/30 hover:bg-terracotta hover:text-white transition-colors mx-1"
                      : "whitespace-nowrap px-2.5 py-2 text-[14px] font-medium text-ink2 hover:text-forest transition-colors"
                  }
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          <div className="flex items-center gap-2.5">
            <a
              href="https://www.zeffy.com/en-CA/donation-form/28-days-to-recovery-sponsor-a-bed-change-a-life"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-donate"
              className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-forest text-forest hover:bg-forest hover:text-sand font-semibold text-sm px-4 py-2 transition-colors"
            >
              <Heart className="w-4 h-4" /> Donate
            </a>
            <Button asChild className="rounded-full bg-terracotta hover:bg-[#b45a3d] text-white font-semibold px-3 sm:px-5 shrink-0">
              <a href={CONTACT.phoneHref} data-testid="nav-call" aria-label="Call us now">
                <Phone className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Call Us Now</span>
              </a>
            </Button>

            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="nav-mobile-trigger" className="xl:hidden text-forest p-1 shrink-0">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-sand border-line w-[320px] overflow-y-auto">
                <div className="mt-8 flex flex-col gap-1">
                  {NAV_LINKS.map((item) => (
                    <div key={item.label}>
                      {item.children ? (
                        <>
                          <p className="text-xs uppercase tracking-[0.18em] text-terracotta mt-4 mb-1 px-1">{item.label}</p>
                          {item.children.map((c) => (
                            <SheetClose asChild key={c.to}>
                              <Link to={c.to} className="block py-2.5 px-1 text-base text-ink2 hover:text-forest border-b border-line/60">
                                {c.label}
                              </Link>
                            </SheetClose>
                          ))}
                        </>
                      ) : (
                        <SheetClose asChild>
                          <Link to={item.to} className="block py-3 px-1 text-lg font-serif text-forest border-b border-line">
                            {item.label}
                          </Link>
                        </SheetClose>
                      )}
                    </div>
                  ))}
                  <SheetClose asChild>
                    <a
                      href="https://www.zeffy.com/en-CA/donation-form/28-days-to-recovery-sponsor-a-bed-change-a-life"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 flex items-center gap-2 text-forest font-semibold"
                    >
                      <Heart className="w-4 h-4" /> Donate
                    </a>
                  </SheetClose>
                  <a href={CONTACT.phoneHref} className="mt-3 flex items-center gap-2 rounded-full bg-terracotta text-white font-semibold px-5 py-3 justify-center">
                    <Phone className="w-4 h-4" /> Call Us Now
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
